import React, { useEffect, useMemo, useState } from "react";
import { http } from "../lib/api";
import { Table as TableIcon, MagnifyingGlass, Download } from "@phosphor-icons/react";

const STATUS_PILL = {
  New: "pill-new",
  Screening: "pill-screening",
  Interview: "pill-interview",
  Offer: "pill-offer",
  Hired: "pill-hired",
  Rejected: "pill-rejected",
};

function OfferPill({ offer, response }) {
  if (!offer) return <span className="text-slate-400 text-xs">—</span>;
  if (response) {
    return (
      <span className={`pill ${response.accepted ? "pill-hired" : "pill-rejected"}`}>
        {response.accepted ? "ACCEPTED" : "DECLINED"}
      </span>
    );
  }
  if (offer.state === "held") return <span className="pill" style={{ background: "#fef3c7", color: "#92400e" }}>ON HOLD</span>;
  if (offer.state === "extended") return <span className="pill pill-offer">EXTENDED</span>;
  return <span className="pill">{(offer.state || "—").toUpperCase()}</span>;
}

export default function RecruitmentDashboard({ statusFilter = "All", locationFilter = "All", requisitionFilter = "All" }) {
  const [candidates, setCandidates] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [q, setQ] = useState("");
  const [offerStateFilter, setOfferStateFilter] = useState("All");
  const [sort, setSort] = useState({ key: "id", dir: "desc" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      http.get("/candidates?includeInactive=true"),
      http.get("/fusion/requisitions").catch(() => ({ data: { items: [] } })),
    ]).then(([c, r]) => {
      if (!alive) return;
      setCandidates(c.data || []);
      setReqs((r.data && r.data.items) || []);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const reqIndex = useMemo(() => {
    const m = {};
    reqs.forEach((r) => { m[String(r.RequisitionNumber)] = r; });
    return m;
  }, [reqs]);

  const rows = useMemo(() => {
    let list = candidates.map((c) => {
      const offer = c.offer || null;
      const response = c.offerResponse || null;
      const reqNum = c.requisitionNumber ? String(c.requisitionNumber) : null;
      const req = reqNum ? reqIndex[reqNum] : null;
      return {
        id: c.id,
        name: c.name,
        email: c.email || "",
        position: c.position || "—",
        location: c.location || "—",
        status: c.status || "—",
        active: c.active !== false,
        requisitionNumber: reqNum || null,
        requisitionTitle: req?.Title || (reqNum ? "Unknown requisition" : null),
        offer,
        offerState: offer ? offer.state : null,
        response,
        onHold: offer?.state === "held",
        recruiterEmail: offer?.extendedBy || null,
        recruiterName: offer?.hold?.heldByName || null,
        offerSalary: offer ? `${offer.salary || ""} ${offer.currency || ""}`.trim() : null,
        offerStartDate: offer?.startDate || null,
        offerExpiresAt: offer?.expiryDate || null,
        holdReason: offer?.hold?.reason || null,
        holdUntil: offer?.hold?.holdUntil || null,
        applicationDate: c.applicationDate,
      };
    });
    if (statusFilter !== "All") list = list.filter((r) => r.status === statusFilter);
    if (locationFilter !== "All") list = list.filter((r) => r.location === locationFilter);
    if (requisitionFilter !== "All") {
      if (requisitionFilter === "__unassigned__") list = list.filter((r) => !r.requisitionNumber);
      else list = list.filter((r) => r.requisitionNumber === String(requisitionFilter));
    }
    if (offerStateFilter !== "All") {
      if (offerStateFilter === "held") list = list.filter((r) => r.onHold);
      else if (offerStateFilter === "extended") list = list.filter((r) => r.offerState === "extended" && !r.response);
      else if (offerStateFilter === "accepted") list = list.filter((r) => r.response?.accepted);
      else if (offerStateFilter === "declined") list = list.filter((r) => r.response && !r.response.accepted);
      else if (offerStateFilter === "no_offer") list = list.filter((r) => !r.offer);
    }
    if (q) {
      const ql = q.toLowerCase();
      list = list.filter((r) =>
        r.name.toLowerCase().includes(ql)
        || r.email.toLowerCase().includes(ql)
        || r.position.toLowerCase().includes(ql)
        || String(r.id).includes(ql)
        || (r.requisitionNumber || "").toLowerCase().includes(ql)
        || (r.requisitionTitle || "").toLowerCase().includes(ql)
        || (r.recruiterEmail || "").toLowerCase().includes(ql),
      );
    }
    const dir = sort.dir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      const av = a[sort.key] ?? "";
      const bv = b[sort.key] ?? "";
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return list;
  }, [candidates, reqIndex, statusFilter, locationFilter, requisitionFilter, offerStateFilter, q, sort]);

  const setSortKey = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };

  const exportCsv = () => {
    const cols = ["id", "name", "email", "position", "location", "status", "requisitionNumber", "requisitionTitle", "offerState", "onHold", "holdReason", "holdUntil", "offerSalary", "offerStartDate", "offerExpiresAt", "recruiterEmail", "applicationDate"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.join(",")].concat(rows.map((r) => cols.map((c) => esc(r[c])).join(","))).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `recruitment-dashboard-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const th = (key, label) => (
    <th
      className="cursor-pointer select-none whitespace-nowrap"
      onClick={() => setSortKey(key)}
      data-testid={`dashboard-th-${key}`}
    >
      {label}
      {sort.key === key && <span className="ml-1 text-slate-400">{sort.dir === "asc" ? "▲" : "▼"}</span>}
    </th>
  );

  // Aggregate stat pills
  const stats = useMemo(() => ({
    total: rows.length,
    extended: rows.filter((r) => r.offerState === "extended" && !r.response).length,
    held: rows.filter((r) => r.onHold).length,
    accepted: rows.filter((r) => r.response?.accepted).length,
    declined: rows.filter((r) => r.response && !r.response.accepted).length,
  }), [rows]);

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="label-eyebrow">Full Pipeline View</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1 flex items-center gap-2">
            <TableIcon size={26} weight="duotone" className="text-[#002FA7]" />
            Recruitment Dashboard
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Every candidate with status, requisition tag, offer state, and the recruiter who owns the offer.
          </p>
        </div>
        <button className="btn btn-outline" onClick={exportCsv} disabled={rows.length === 0} data-testid="dashboard-export-csv">
          <Download size={14} weight="bold" /> Export CSV
        </button>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Candidates" value={stats.total} testid="stat-total" />
        <StatCard label="Offer Extended" value={stats.extended} tone="offer" testid="stat-extended" />
        <StatCard label="On Hold" value={stats.held} tone="hold" testid="stat-held" />
        <StatCard label="Accepted" value={stats.accepted} tone="hired" testid="stat-accepted" />
        <StatCard label="Declined" value={stats.declined} tone="rejected" testid="stat-declined" />
      </div>

      {/* Local filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[280px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="dashboard-search"
            className="input !pl-8"
            placeholder="Search by name, ID, email, position, requisition, or recruiter…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500">Offer state</label>
          <select
            data-testid="dashboard-offer-state"
            className="input !py-1.5 !text-xs w-40"
            value={offerStateFilter}
            onChange={(e) => setOfferStateFilter(e.target.value)}
          >
            <option value="All">All</option>
            <option value="no_offer">No offer yet</option>
            <option value="extended">Extended (pending)</option>
            <option value="held">On hold</option>
            <option value="accepted">Accepted</option>
            <option value="declined">Declined</option>
          </select>
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="data-table" data-testid="dashboard-table">
          <thead>
            <tr>
              {th("id", "ID")}
              {th("name", "Candidate")}
              {th("position", "Position")}
              {th("requisitionNumber", "Requisition")}
              {th("status", "Status")}
              {th("offerState", "Offer")}
              {th("recruiterEmail", "Recruiter")}
              {th("applicationDate", "Applied")}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center py-10 text-slate-400 text-sm">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-slate-400 text-sm">
                No candidates match the current filters.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className={r.active ? "" : "opacity-60"} data-testid={`dashboard-row-${r.id}`}>
                <td className="font-mono text-xs">#{String(r.id).padStart(3, "0")}</td>
                <td>
                  <div className="font-medium">{r.name}</div>
                  <div className="text-[11px] text-slate-500">{r.email || "—"}</div>
                </td>
                <td>
                  <div>{r.position}</div>
                  <div className="text-[11px] text-slate-500">{r.location}</div>
                </td>
                <td>
                  {r.requisitionNumber ? (
                    <div>
                      <div className="font-mono text-xs">#{r.requisitionNumber}</div>
                      <div className="text-[11px] text-slate-500 truncate max-w-[160px]" title={r.requisitionTitle}>{r.requisitionTitle}</div>
                    </div>
                  ) : (
                    <span className="text-[11px] text-slate-400 italic">Unassigned</span>
                  )}
                </td>
                <td><span className={`pill ${STATUS_PILL[r.status] || ""}`}>{r.status?.toUpperCase()}</span></td>
                <td>
                  <OfferPill offer={r.offer} response={r.response} />
                  {r.onHold && r.holdReason && (
                    <div className="text-[10px] text-amber-800 mt-1 max-w-[180px] leading-tight" title={r.holdReason}>
                      {r.holdReason}
                      {r.holdUntil && <span className="text-slate-500"> · until {r.holdUntil}</span>}
                    </div>
                  )}
                  {r.offer && !r.onHold && !r.response && r.offerSalary && (
                    <div className="text-[10px] text-slate-500 mt-1">{r.offerSalary}</div>
                  )}
                </td>
                <td>
                  {r.recruiterEmail ? (
                    <div className="text-xs">{r.recruiterEmail}</div>
                  ) : (
                    <span className="text-[11px] text-slate-400">—</span>
                  )}
                </td>
                <td className="text-xs text-slate-600 whitespace-nowrap">{r.applicationDate || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  const bg = {
    offer: "bg-indigo-50 border-indigo-100 text-indigo-900",
    hold: "bg-amber-50 border-amber-100 text-amber-900",
    hired: "bg-emerald-50 border-emerald-100 text-emerald-900",
    rejected: "bg-red-50 border-red-100 text-red-900",
  }[tone] || "bg-white border-slate-200 text-slate-800";
  return (
    <div className={`rounded-md border px-4 py-3 ${bg}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-2xl font-display font-bold mt-1">{value}</div>
    </div>
  );
}
